import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import {
  UssdOperator,
  UssdServiceRequest,
  UssdServiceResponse,
} from '../../types/ussd.models';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class UssdService {
  private http = inject(HttpClient);
  private baseUrl = environment.ussdApiUrl;

  sendRequest(request: UssdServiceRequest): Observable<UssdServiceResponse> {
    return this.http.post<UssdServiceResponse>(
      `${this.baseUrl}/services/blackkonnectussdaggregator/api/ussd/simulator/request`,
      request
    );
  }

  /**
   * Networks the simulator can stand in for in one country, for the operator
   * picker. The backend answers 404 when it has none for the country.
   * Served at /services/blackkonnectussdaggregator/api/ussd/simulator/telcos/{cc}
   * (the older helpers below call a path the backend does not serve).
   */
  getOperators(countryCode: string): Observable<UssdOperator[]> {
    return this.http
      .get<unknown>(
        `${this.baseUrl}/services/blackkonnectussdaggregator/api/ussd/simulator/telcos/${encodeURIComponent(countryCode)}`
      )
      .pipe(map((body) => toOperators(body)));
  }

  /** @deprecated Calls a path the backend does not serve; unused. */
  sendTelcoRequest(
    countryCode: string,
    operatorCode: string,
    request: UssdServiceRequest
  ): Observable<UssdServiceResponse> {
    return this.http.post<UssdServiceResponse>(
      `${this.baseUrl}/simulator/${countryCode}/${operatorCode}`,
      request
    );
  }

  /** @deprecated Wrong path; use getOperators. */
  getTelcosByCountry(countryCode: string): Observable<unknown> {
    return this.http.get(`${this.baseUrl}/simulator/telcos/${countryCode}`);
  }

  getAllTelcos(): Observable<unknown> {
    return this.http.get(`${this.baseUrl}/simulator/telcos`);
  }

  getSupportedCountries(): Observable<unknown> {
    return this.http.get(`${this.baseUrl}/simulator/countries`);
  }

  generateSessionId(): string {
    return `session-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  }

  createInitialRequest(
    msisdn: string,
    serviceCode: string,
    sessionId: string,
    operator?: string,
    operatorCountryCode?: string
  ): UssdServiceRequest {
    return {
      msisdn,
      serviceCode,
      sessionId,
      serviceRequestType: 'New',
      serviceData: '',
      operator,
      operatorCountryCode,
    };
  }

  createContinueRequest(
    msisdn: string,
    serviceCode: string,
    sessionId: string,
    userInput: string,
    operator?: string,
    operatorCountryCode?: string
  ): UssdServiceRequest {
    return {
      msisdn,
      serviceCode,
      sessionId,
      serviceRequestType: 'Continue',
      serviceData: userInput,
      operator,
      operatorCountryCode,
    };
  }

  createEndRequest(
    msisdn: string,
    serviceCode: string,
    sessionId: string,
    operator?: string,
    operatorCountryCode?: string
  ): UssdServiceRequest {
    return {
      msisdn,
      serviceCode,
      sessionId,
      serviceRequestType: 'Release',
      serviceData: '',
      operator,
      operatorCountryCode,
    };
  }

  hasErrorMetadata(response: UssdServiceResponse): boolean {
    return (
      response.metadata?.['error.occurred'] === true ||
      response.metadata?.['error.occurred'] === 'true'
    );
  }
}

/** TelcoInfo[] (or a wrapper around it) → operator choices, tolerant of field names. */
function toOperators(body: unknown): UssdOperator[] {
  const list = Array.isArray(body)
    ? body
    : Array.isArray((body as { content?: unknown })?.content)
      ? (body as { content: unknown[] }).content
      : [];
  const seen = new Set<string>();
  const operators: UssdOperator[] = [];
  for (const item of list) {
    if (typeof item === 'string') {
      if (!seen.has(item)) {
        seen.add(item);
        operators.push({ code: item, name: item });
      }
      continue;
    }
    if (!item || typeof item !== 'object') continue;
    const record = item as Record<string, unknown>;
    // TelcoInfo.uniqueCode is what the backend matches `operator` against.
    const code = String(
      record['uniqueCode'] ??
        record['operatorCode'] ??
        record['code'] ??
        record['operator'] ??
        record['telcoCode'] ??
        record['id'] ??
        ''
    ).trim();
    if (!code || seen.has(code)) continue;
    seen.add(code);
    const name = String(
      record['operatorName'] ?? record['name'] ?? record['displayName'] ?? record['telcoName'] ?? code
    ).trim();
    operators.push({ code, name: name || code });
  }
  return operators;
}
